import { estypes } from "@elastic/elasticsearch";
import { NestedESProperty } from "utils/constants/elasticsearchConfig";

export function formatNestedFieldQuery(
  nestedField: NestedESProperty,
  searchTerm: string,
  _maximumNestedFieldValues: number
): estypes.QueryDslQueryContainer[] {
  if (!nestedField.nestedFields || nestedField.nestedFields.length === 0) {
    // If there are no nested fields, return an empty array
    return [];
  }

  if (nestedField.nestedFields.every((field) => !field.searchEnabled)) {
    // If all nested fields are disabled for search, return an empty array
    return [];
  }

  const nestedFields = nestedField.nestedFields.filter(
    (field) => field.searchEnabled
  );
  if (nestedFields.length === 0) {
    // If there are no nested fields enabled for search, return an empty array
    return [];
  }

  // List of fields as strings with weights. E.g. "codes.name_norwegian^3" Format: propertyName.nestedFieldName^weight
  const nestedFieldsWithWeights = nestedFields.map(
    (field) =>
      `${nestedField.propertyName}.${field.propertyName}^${field.weight}`
  );

  // Combined nested query with both best_fields and phrase matching
  // Using a single nested query with a bool should clause to combine both match types
  // This prevents score doubling that occurs when using two separate nested queries in dis_max
  const combinedNestedQuery: estypes.QueryDslQueryContainer = {
    nested: {
      path: nestedField.propertyName,
      score_mode: "avg",
      query: {
        bool: {
          should: [
            // Best field query: only counts the score of the field with the highest independent score
            {
              multi_match: {
                query: "" + searchTerm,
                type: "best_fields",
                fields: nestedFieldsWithWeights
              }
            },
            // Phrase query: gives boost when search terms match in exact order
            // This will add to the score but within the same nested context
            {
              multi_match: {
                query: "" + searchTerm,
                type: "phrase",
                fields: nestedFieldsWithWeights
              }
            }
          ],
          minimum_should_match: 1
        }
      },
      boost: 0.7
    }
  };

  // Add inner_hits if there are fields to return in results
  const nestedFieldsToReturn = nestedField.nestedFields.filter(
    (field) => field.returnInResults
  );

  if (nestedFieldsToReturn.length > 0 && combinedNestedQuery.nested) {
    const innerHitsFields: Record<string, estypes.SearchHighlightField> = {};
    for (const field of nestedFieldsToReturn) {
      innerHitsFields[`${nestedField.propertyName}.${field.propertyName}`] = {};
    }

    combinedNestedQuery.nested.inner_hits = {
      highlight: {
        fields: innerHitsFields
      },
      size: 50
    };
  }

  return [combinedNestedQuery];
}
